import type { PlantNetworkTreeNode } from '@axilon-apps/domains-ontology-ui-state'

/** Stable expansion keys for the kind-based Network dropdowns. */
export const NETWORK_SECTION_ASSETS = '__network_section:assets'
export const NETWORK_SECTION_SEGMENTS = '__network_section:segments'
export const NETWORK_SECTION_UNASSIGNED = '__network_section:unassigned'

export interface NetworkTreeSectionBucket {
  id: string
  label: string
  nodes: PlantNetworkTreeNode[]
}

/** True when any tree row carries an Experion EMDB `EmdbProject` membership. */
export function networkTreeHasProjects(nodes: readonly PlantNetworkTreeNode[]): boolean {
  return nodes.some((node) => typeof node.projectId === 'string' && node.projectId.length > 0)
}

/** Rail label: Experion `EntityName` (`display_name`) over the StrategyName `name`. */
export function networkTreeLabel(
  node: Pick<PlantNetworkTreeNode, 'name' | 'display_name'>
): string {
  const display = node.display_name?.trim()
  return display || node.name
}

/**
 * Partition Network tree nodes by `EmdbProject`. Assigned projects sort by
 * name; nodes with no `projectId` land in Unassigned last. Callers rebuild
 * the CONTAINS tree per bucket so a child whose parent is in another project
 * surfaces as a root.
 */
export function groupNetworkNodesByProject(
  nodes: readonly PlantNetworkTreeNode[]
): NetworkTreeSectionBucket[] {
  const groups = new Map<string, { label: string; nodes: PlantNetworkTreeNode[] }>()
  for (const node of nodes) {
    const projectId = node.projectId?.trim()
    const id = projectId || NETWORK_SECTION_UNASSIGNED
    const label = projectId ? node.projectName?.trim() || 'Untitled project' : 'Unassigned'
    const group = groups.get(id)
    if (group) group.nodes.push(node)
    else groups.set(id, { label, nodes: [node] })
  }

  const assigned = [...groups.entries()]
    .filter(([id]) => id !== NETWORK_SECTION_UNASSIGNED)
    .sort((a, b) => a[1].label.localeCompare(b[1].label, undefined, { sensitivity: 'base' }))
    .map(([id, group]) => ({
      id: `__network_section:project:${id}`,
      label: group.label,
      nodes: group.nodes
    }))

  const unassigned = groups.get(NETWORK_SECTION_UNASSIGNED)
  if (unassigned) {
    assigned.push({
      id: NETWORK_SECTION_UNASSIGNED,
      label: unassigned.label,
      nodes: unassigned.nodes
    })
  }
  return assigned
}
